import React, { useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { Dropdown, DropdownBody } from '../../../components/dropdown';
import { ReactComponent as DotsIconSvg } from '../../../shared/icons/dotsRoundIcon.svg';

const DotsIcon = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>((props, ref) => (
  <span {...props} ref={ref} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><DotsIconSvg /></span>
));
import { paths } from '../../../routing';
import { useDropdownControl } from '../../../contexts/DropdownContext';
import styles from './dealControls.module.scss';
import MoveDealModal from './modals/MoveDealModal';

type DealControlsProps = {
  dealId: string;
  dealTitle: string;
  currentFolderId?: string;
  editPermission: boolean;
  managePermission?: boolean;
  teamId: string;
  onSuccess: () => void;
};

type DropdownActionsType = {
  text: string;
  onClick: () => void;
  isAvailable: boolean;
};

enum DealActionStatus {
  view = 'View',
  move = 'Move to Folder',
}

const DealControls = ({
  dealId,
  dealTitle,
  currentFolderId,
  editPermission,
  managePermission,
  teamId,
  onSuccess,
}: DealControlsProps) => {
  const dropdownId = useMemo(() => `deal-dropdown-${dealId}`, [dealId]);
  const { isOpen, onToggle } = useDropdownControl(dropdownId);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const navigate = useNavigate();
  const isManageAvailable = managePermission ?? editPermission;

  const navigateToDeal = useCallback(
    (dealId: string, teamId: string) => {
      const dealUrl = paths.deals.detail.replace(':teamId', teamId).replace(':dealId', dealId);
      navigate(dealUrl);
    },
    [navigate]
  );

  const allDropDownActions = [
    {
      text: DealActionStatus.view,
      onClick: () => {
        navigateToDeal(dealId, teamId);
      },
      isAvailable: true,
    },
    {
      text: DealActionStatus.move,
      onClick: () => {
        setShowMoveModal(true);
      },
      isAvailable: isManageAvailable,
    },
  ];

  const dropDownActions = (actions: DropdownActionsType[]) => {
    return actions
      .filter((action) => action.isAvailable)
      .map((action, index) => (
        <Dropdown.Item as={Button} variant="link" onClick={action.onClick} key={index}>
          {action.text}
        </Dropdown.Item>
      ));
  };

  return (
    <>
      <Dropdown show={isOpen} onToggle={onToggle}>
        <Dropdown.Toggle id="dropdown-deal-actions" className={styles.dottedDropdown} as={DotsIcon} />
        <Dropdown.Menu align="right">
          <DropdownBody>{dropDownActions(allDropDownActions)}</DropdownBody>
        </Dropdown.Menu>
      </Dropdown>

      <MoveDealModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        dealId={dealId}
        dealTitle={dealTitle}
        currentFolderId={currentFolderId}
        teamId={teamId}
        onSuccess={onSuccess}
      />
    </>
  );
};

export default DealControls;
